import React, { useEffect, useState } from "react";
import { Truck, Check } from "lucide-react";
import api from "../lib/api";

/**
 * Slim banner that shows how close the projected cart subtotal is to a
 * threshold-based free-shipping promo (e.g. "Add A$70 more to unlock free Express").
 *
 * Self-contained: fetches available shipping methods once and picks the first
 * one with `free_above > 0`. Pass `projectedSubtotal` (cart subtotal + the
 * current product's unit price × qty) so the banner updates as the user
 * changes quantity on a product page.
 */
export const FreeShippingNudge = ({ projectedSubtotal, className = "" }) => {
    const [promoMethod, setPromoMethod] = useState(null);

    useEffect(() => {
        let cancelled = false;
        api.get("/shipping-methods")
            .then((r) => {
                if (cancelled) return;
                const list = Array.isArray(r.data) ? r.data : [];
                const candidate = list.find((m) => Number(m.free_above) > 0);
                setPromoMethod(candidate || null);
            })
            .catch(() => { if (!cancelled) setPromoMethod(null); });
        return () => { cancelled = true; };
    }, []);

    if (!promoMethod) return null;
    const threshold = Number(promoMethod.free_above);
    const sub = Math.max(0, Number(projectedSubtotal || 0));
    const remaining = Math.max(0, threshold - sub);
    const unlocked = remaining <= 0;
    const progressPct = Math.min(100, Math.round((sub / threshold) * 100));

    return (
        <div
            className={`p-3 rounded-xl border ${
                unlocked
                    ? "border-emerald-500/40 bg-emerald-500/5"
                    : "border-[var(--gold)]/30 bg-[rgba(233,188,11,0.05)]"
            } ${className}`}
            data-testid="product-free-shipping-nudge"
        >
            <div className="flex items-center gap-2 mb-2">
                {unlocked ? (
                    <Check size={14} className="text-emerald-300 shrink-0" />
                ) : (
                    <Truck size={14} className="text-[var(--gold)] shrink-0" />
                )}
                <span className="text-xs text-white font-semibold leading-tight">
                    {unlocked ? (
                        <>
                            Free <span className="text-emerald-300">{promoMethod.name}</span> unlocked at checkout
                        </>
                    ) : (
                        <>
                            Add{" "}
                            <span
                                className="text-[var(--gold)] font-mono font-bold"
                                data-testid="product-nudge-remaining"
                            >
                                A${remaining.toFixed(2)}
                            </span>{" "}
                            more to unlock free <span className="text-[var(--gold)]">{promoMethod.name}</span>
                        </>
                    )}
                </span>
            </div>
            <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${
                        unlocked
                            ? "bg-gradient-to-r from-emerald-400 to-emerald-300"
                            : "bg-gradient-to-r from-[var(--gold)] to-amber-200"
                    }`}
                    style={{ width: `${progressPct}%` }}
                    data-testid="product-nudge-bar"
                />
            </div>
        </div>
    );
};
